import { Seo } from "../components/Seo";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  LineChart,
  Building2,
  Megaphone,
  FileVideo,
  Store,
  ShieldCheck,
  ChevronRight,
  CheckCircle2,
  Users,
  DollarSign,
  Target,
  Wallet
} from "lucide-react";

const useCasesData = [
  {
    id: "defi",
    name: "DeFi & Trading",
    icon: LineChart,
    color: "swarm-violet",
    buyer: "Chief Trader, Fund Manager",
    treasuryMode: "Distribution",
    tam: "$7B+",
    problem: "Trading agents executing without oversight create unbounded risk exposure and compliance nightmares.",
    solution: "SwarmCitadel governance enables tap rules, velocity caps, and real-time audit trails for every trade.",
    workflow: [
      "Agent proposes trade action",
      "Policy engine evaluates against tap rules",
      "Approval quorum validates large transactions",
      "Trade executes with full audit trail",
      "Revenue distributed via treasury engine"
    ],
    features: [
      "Position limits enforcement",
      "Cross-margin monitoring",
      "Regulatory reporting",
      "P&L attribution"
    ],
    video: "https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/cui8422v_2026-03-22_12-07-47.mp4"
  },
  {
    id: "enterprise",
    name: "Enterprise AI Ops",
    icon: Building2,
    color: "swarm-cyan",
    buyer: "CFO, CTO, COO",
    treasuryMode: "Budget Control",
    tam: "$93B by 2032",
    problem: "Enterprise AI deployments lack centralized governance, creating shadow AI and runaway costs.",
    solution: "Budget controls, compliance mapping, and CISO agent monitoring for institutional AI operations.",
    workflow: [
      "Agent registered with budget allocation",
      "Actions evaluated against spending policies",
      "CISO agent monitors compliance posture",
      "Budget rollup across org hierarchy",
      "Executive dashboards track utilization"
    ],
    features: [
      "Department-level budgets",
      "Cost center attribution",
      "Compliance automation",
      "Executive reporting"
    ]
  },
  {
    id: "marketing",
    name: "Marketing & SEM",
    icon: Megaphone,
    color: "swarm-green",
    buyer: "CMO, Head of Growth",
    treasuryMode: "Budget Control",
    tam: "$620B",
    problem: "Marketing agents can overspend budgets and violate brand guidelines without proper controls.",
    solution: "Budget control mode with velocity caps ensures ad spend stays within limits while maximizing ROI.",
    workflow: [
      "Campaign agent initialized with budget",
      "Bid strategies evaluated against policies",
      "Spending velocity monitored in real-time",
      "Brand compliance checked per action",
      "Performance attributed and reported"
    ],
    features: [
      "Campaign budget caps",
      "Brand safety policies",
      "Multi-channel attribution",
      "ROAS optimization rules"
    ]
  },
  {
    id: "sports",
    name: "Sports & Talent",
    icon: FileVideo,
    color: "swarm-violet",
    buyer: "Sports Director, Talent Manager",
    treasuryMode: "Budget Control",
    tam: "$30B+",
    problem: "Sports organizations manage player contracts, scouting budgets, and academy operations across multiple departments with no unified AI governance.",
    solution: "Budget controls per department, player contract KPI monitoring, scouting agent marketplace, transfer compliance and wage cap enforcement with full audit trails.",
    workflow: [
      "Scouting agent evaluates talent",
      "Budget policies enforce department limits",
      "Contract KPIs monitored automatically",
      "Transfer compliance verified",
      "Wage cap enforcement tracked"
    ],
    features: [
      "Department budgets",
      "Player contract KPIs",
      "Transfer compliance",
      "Wage cap monitoring"
    ]
  },
  {
    id: "yield",
    name: "Yield & Treasury",
    icon: Store,
    color: "swarm-cyan",
    buyer: "Treasury Manager, Portfolio Manager",
    treasuryMode: "Distribution",
    tam: "$50T+ AUM",
    problem: "Yield-producing agents across crypto, real estate, and precious metals operate without spending controls or portfolio governance.",
    solution: "Any yield-producing agent governed: automated rebalancing, diversification rules, risk limits. Crypto yield, real estate income, precious metals — all under one policy engine.",
    workflow: [
      "Yield agent proposes allocation",
      "Diversification rules evaluated",
      "Risk limits enforced",
      "Rebalancing automated",
      "Yields distributed per policy"
    ],
    features: [
      "Multi-asset governance",
      "Risk limit enforcement",
      "Auto-rebalancing",
      "Yield distribution"
    ]
  },
  {
    id: "government",
    name: "Government & Sovereign Funds",
    icon: ShieldCheck,
    color: "swarm-green",
    buyer: "Minister, Fund Director, GRC Lead",
    treasuryMode: "Budget Control",
    tam: "$3T+ AUM",
    problem: "Sovereign wealth funds and government entities require multi-level approval chains, GRC compliance, and data sovereignty for any AI agent deployment.",
    solution: "Board-to-CEO delegation of authority mirroring real org structures. Shariah compliance, budget ceiling enforcement, internal audit trails, and data sovereignty controls for national AI programs.",
    workflow: [
      "Agent action proposed",
      "Multi-level approvals triggered",
      "GRC compliance verified",
      "Data sovereignty enforced",
      "Audit trail maintained"
    ],
    features: [
      "Multi-level approvals",
      "Shariah compliance",
      "Data sovereignty",
      "Internal audit"
    ],
    highlight: true
  },
  {
    id: "x402",
    name: "x402 Agent Payments",
    icon: Wallet,
    color: "swarm-cyan",
    buyer: "CFO, Treasury, Compliance",
    treasuryMode: "Budget Control",
    tam: "",
    problem: "AI agents paying APIs via x402 have no spend policy, budget limits, or audit trail. Finance has no visibility until wallets are drained.",
    solution: "Policy enforcement before signature for agents paying APIs over the open x402 standard. Budgets, allowlists, reasoning gate, audit trail.",
    workflow: [
      "Agent receives x402 payment challenge",
      "Challenge forwarded to SwarmCitadel treasury",
      "Policy engine evaluates against rules",
      "Approved payments signed from governed vault",
      "Denied payments escalate to human",
      "Every decision written to audit trail"
    ],
    features: [
      "Per-call and daily budgets",
      "Counterparty allowlists",
      "Reasoning Quality Gate",
      "On-chain audit linkage"
    ],
    highlight: true,
    inDevelopment: true,
    link: "/x402"
  }
];

const VALID_TABS = useCasesData.map((u) => u.id);

export const UseCasesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const hashTab = location.hash.replace("#", "");
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(hashTab) ? hashTab : "defi");

  useEffect(() => {
    if (VALID_TABS.includes(hashTab)) {
      setActiveTab(hashTab);
      requestAnimationFrame(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [hashTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    navigate({ pathname: location.pathname, hash: value }, { replace: true });
  };

  return (
    <div className="pt-24" data-testid="use-cases-page">
      <Seo title="Use Cases: AI Agent Governance Across Verticals | SwarmCitadel" description="Institutional-grade AI agent governance for DeFi trading, enterprise AI ops, treasury, sovereign funds, sports & talent, and x402 agent payments." path="/use-cases" />
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-swarm-cyan/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="section-label">Use Cases</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              Built for Every
              <br />
              <span className="text-gradient inline-block">Vertical</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              From DeFi trading desks to enterprise AI ops, SwarmCitadel provides 
              institutional-grade governance across industries.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Tabs */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 scroll-mt-28" ref={tabsRef}>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 h-auto gap-2 p-0 bg-transparent mb-12" data-testid="usecase-tabs-list">
              {useCasesData.map((useCase) => {
                const Icon = useCase.icon;
                return (
                  <TabsTrigger
                    key={useCase.id}
                    value={useCase.id}
                    className="flex flex-col items-center justify-center gap-2 px-3 py-4 h-full whitespace-normal text-center leading-tight text-slate-400 bg-swarm-card/40 hover:text-white hover:border-slate-500 data-[state=active]:bg-swarm-violet/20 data-[state=active]:text-swarm-violet border border-swarm-border data-[state=active]:border-swarm-violet/30 rounded-xl transition-colors"
                    data-testid={`usecase-tab-${useCase.id}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="text-xs sm:text-sm font-medium">{useCase.name}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {useCasesData.map((useCase) => {
              const Icon = useCase.icon;
              const colorClasses = {
                'swarm-violet': { bg: 'bg-swarm-violet/10', text: 'text-swarm-violet', border: 'border-swarm-violet/30' },
                'swarm-cyan': { bg: 'bg-swarm-cyan/10', text: 'text-swarm-cyan', border: 'border-swarm-cyan/30' },
                'swarm-green': { bg: 'bg-swarm-green/10', text: 'text-swarm-green', border: 'border-swarm-green/30' }
              };
              const colors = colorClasses[useCase.color];

              return (
                <TabsContent key={useCase.id} value={useCase.id}>
                  <div className={`glass-card p-8 md:p-12 ${useCase.highlight ? colors.border : ''}`}>
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
                      <div className={`p-4 rounded-2xl ${colors.bg}`}>
                        <Icon className={`w-10 h-10 ${colors.text}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="text-3xl font-bold text-white">{useCase.name}</h2>
                          {useCase.inDevelopment && (
                            <span className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-500 text-xs font-medium rounded-full">
                              In development
                            </span>
                          )}
                        </div>
                        <p className="text-slate-400 text-lg">{useCase.solution}</p>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {useCase.tam && (
                          <div className="glass-card p-4 text-center">
                            <div className="text-xs text-slate-500 mb-1">TAM</div>
                            <div className={`font-mono font-bold ${colors.text}`}>{useCase.tam}</div>
                          </div>
                        )}
                        <div className="glass-card p-4 text-center">
                          <div className="text-xs text-slate-500 mb-1">Treasury Mode</div>
                          <div className="text-white font-medium">{useCase.treasuryMode}</div>
                        </div>
                      </div>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                      <div className="flex items-start gap-3">
                        <Users className={`w-5 h-5 ${colors.text} mt-1`} />
                        <div>
                          <div className="text-slate-500 text-sm">Target Buyer</div>
                          <div className="text-white">{useCase.buyer}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Target className={`w-5 h-5 ${colors.text} mt-1`} />
                        <div>
                          <div className="text-slate-500 text-sm">Problem</div>
                          <div className="text-white text-sm">{useCase.problem}</div>
                        </div>
                      </div>
                      {useCase.tam && (
                      <div className="flex items-start gap-3">
                        <DollarSign className={`w-5 h-5 ${colors.text} mt-1`} />
                        <div>
                          <div className="text-slate-500 text-sm">Market Size</div>
                          <div className="text-white">{useCase.tam}</div>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Workflow & Features */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Workflow */}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-4">Example Workflow</h3>
                        <div className="space-y-3">
                          {useCase.workflow.map((step, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <div className={`w-6 h-6 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center text-xs font-mono flex-shrink-0`}>
                                {index + 1}
                              </div>
                              <span className="text-slate-300 text-sm">{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Features */}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-4">Key Features</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {useCase.features.map((feature) => (
                            <div key={feature} className="flex items-center gap-2">
                              <CheckCircle2 className={`w-4 h-4 ${colors.text}`} />
                              <span className="text-slate-300 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-10 pt-8 border-t border-swarm-border flex flex-col sm:flex-row gap-4 justify-between items-center">
                      <p className="text-slate-400 text-sm">
                        {useCase.inDevelopment 
                          ? `Interested in ${useCase.name.toLowerCase()}? Request early access.`
                          : `Ready to implement ${useCase.name.toLowerCase()} governance?`
                        }
                      </p>
                      <div className="flex gap-3">
                        {useCase.link ? (
                          <Link to={useCase.link}>
                            <Button variant="outline" className="btn-outline">
                              Learn More
                            </Button>
                          </Link>
                        ) : (
                          <Link to="/docs">
                            <Button variant="outline" className="btn-outline">
                              View Template
                            </Button>
                          </Link>
                        )}
                        <Link to="/contact">
                          <Button className="bg-swarm-violet hover:bg-swarm-violet/90">
                            {useCase.inDevelopment ? 'Request Demo' : 'Talk to Sales'} <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {/* Video Section - only for use cases with video */}
                    {useCase.video && (
                      <div className="mt-10 pt-8 border-t border-swarm-border">
                        <h3 className="text-lg font-semibold text-white mb-4">OpsControl Demo</h3>
                        <div className="rounded-xl overflow-hidden border border-swarm-border bg-swarm-darker">
                          <video 
                            controls
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            className="w-full aspect-video"
                            data-testid="usecase-video"
                          >
                            <source src={useCase.video} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                        <p className="text-slate-500 text-sm mt-3 text-center">
                          Real-time trading agent monitoring and governance dashboard
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Don't see your use case?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            SwarmCitadel is built to govern any agent workflow. Let's discuss your specific needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-glow" data-testid="usecase-bottom-cta">
              Schedule a Call <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default UseCasesPage;
